import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Button, Checkbox, HStack, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import PINSAIAssistantLabel from '../../../../../../../../../../components/PINSAIAssistantLabel';
import { createErrorToast } from '../../../../../../../../../../redux/modules/app';
import { hideModal, showModal } from '../../../../../../../../../../redux/modules/modal';
import { useActiveRecordDocuments, useAiScanDocs } from '../../../../../../../../../../services/record-service';
import ScanProgressBar from '../../../QuestionRequirementIconButton/ScanProgressBar';
import FileNameCell from '../DocumentsTableContainer/components/DocumentsTable/cells/FileNameCell';

const AiRescanModal = ({ recordId }) => {
  const dispatch = useDispatch();
  const { data: activeRecordDocuments, isLoading } = useActiveRecordDocuments({ recordId });
  const { mutateAsync: initializeAiScan } = useAiScanDocs({ recordId });

  const uniqueDocs = useMemo(() => {
    if (isLoading) {
      return [];
    }

    // remove duplicate documents across different document types
    return [...new Map(activeRecordDocuments.map(doc => [doc.fileUniqueId ?? doc.displayName ?? doc.originalName, doc])).values()];
  }, [activeRecordDocuments]);

  const [selectedDocs, setSelectedDocs] = useState({});

  const handleDocCheck = (docId, checked) => {
    setSelectedDocs(prev => ({
      ...prev,
      [docId]: checked
    }));
  };

  const handleSubmit = async () => {
    const selectedDocIds = Object.keys(selectedDocs)
      .filter(docId => selectedDocs[docId])
      .map(docId => parseInt(docId, 10));

    if (selectedDocIds.length === 0) {
      dispatch(createErrorToast({ title: 'Please select at least one document to rescan.' }));
      return;
    }

    const resp = await initializeAiScan({ docIds: selectedDocIds });

    dispatch(hideModal());
    dispatch(showModal(
      () => <ScanProgressBar gptRequestId={resp.gptRequestId} recordId={recordId} />,
      { size: '2xl' }
    ));
  };

  return (
    <Box my={4} textAlign="left">
      <PINSAIAssistantLabel mb={8} w="300px" />
      <Text mb={4}>Select which documents you would like to use for this scan:</Text>

      <Box my="auto">
        {uniqueDocs?.map(document => (
          <HStack key={document.id} mb={4} spacing={4}>
            <Checkbox
              onChange={e => handleDocCheck(document.id, e.target.checked)}
            />
            <FileNameCell
              hideOptions
              document={document}
              key={document.id}
              recordId={recordId}
            />
          </HStack>
        ))}
      </Box>

      <HStack justifyContent="center" mt={4}>
        <Button
          variant="secondary"
          onClick={() => dispatch(hideModal())}
        >
          Cancel
        </Button>

        <Button
          variant="primary"
          onClick={handleSubmit}
        >
          Submit
        </Button>
      </HStack>
    </Box>
  );
};

AiRescanModal.propTypes = { recordId: PropTypes.number.isRequired };

export default AiRescanModal;
